import { RequireAtLeastOne } from 'type-fest';
import {
  MasonryEngineNode,
  NodeQueryMeta,
} from '#types/masonry-engine-node.types.ts';
import { QUERY_KEY_GET_NODE_ASYNC_SELF } from '#constants/constants.ts';
import { QueryClient, QueryFilters } from '@tanstack/react-query';

const isMasonryNodeMeta = (meta: unknown): meta is NodeQueryMeta =>
  !!(meta && typeof meta === 'object' && 'ancestry' in meta);

type ClearNodeCacheOptions = {
  id: MasonryEngineNode['id'];
  queryClient: QueryClient;
  strategy: 'remove' | 'invalidate';
} & RequireAtLeastOne<{
  self?: boolean;
  descendants?: boolean;
}>;

const clear = (
  queryClient: QueryClient,
  strategy: 'remove' | 'invalidate',
  filters: QueryFilters,
) =>
  strategy === 'remove'
    ? queryClient.removeQueries(filters)
    : queryClient.invalidateQueries(filters);
/**
 * Given a Node ID, clears the cache for that node's async data and/or its descendants' async data.
 * Note: if this function is called while any of the aforementioned nodes are mounted, they will re-fetch
 * immediately.
 */
export const clearNodeCache = ({
  id,
  queryClient,
  strategy,
  self,
  descendants,
}: ClearNodeCacheOptions) => {
  if (self) {
    clear(queryClient, strategy, {
      queryKey: [QUERY_KEY_GET_NODE_ASYNC_SELF, id],
    });
  }

  if (descendants) {
    clear(queryClient, strategy, {
      queryKey: [QUERY_KEY_GET_NODE_ASYNC_SELF],
      predicate: ({ meta }) => {
        if (isMasonryNodeMeta(meta)) {
          const [, ...upperAncestry] = (meta as NodeQueryMeta).ancestry;
          return upperAncestry.includes(id);
        }

        return false;
      },
    });
  }
};
